import { arrayUnion, doc, getDoc, getFirestore, writeBatch } from 'firebase/firestore';
import Styles from './styles.module.css';
import React, { useEffect, useState } from 'react';
import { schoolList } from 'src/type';
import { loadSendingWhitelist } from '../../../3.recruit/navigation/publishLogic';
import { useNavigate } from 'react-router-dom';

type PopModalProps = {
  companyId: string,
  // 送付先を保存する年度(卒業年度)。`${year}_sending` に保存する。
  year: string,
  schools: string[],
  // 送付が完了した後の移動先。初期設定(STEP4)は完了画面、年度一覧から開いた送付画面は年度一覧。
  // (初期設定を終えた会社が /service/manual/* に入ると desktop/index.tsx のパス検証で
  //  ホーム(無料プランはサロンPR)へ戻されるため、年度一覧からは完了画面へ飛ばさない)
  completePath: string,
  setSendModal: React.Dispatch<React.SetStateAction<boolean>>;
  setCheckList: React.Dispatch<React.SetStateAction<string[]>>;
};

const PopModal: React.FC<PopModalProps> = ({ companyId, year, schools, completePath, setSendModal, setCheckList}) => {

  const [ selectedNames, setSelectedNames ] = useState<string[]>([]);

  useEffect(() => {
    const schoolMap = new Map(schoolList);
    const name: string[] = schools.map((id: string) => schoolMap.get(id)).filter((name: any): name is string => Boolean(name));
    setSelectedNames(name);
  }, [ ]);

  const navigate = useNavigate();

  const [ sending, setSending ] = useState<boolean>(false);
  const [ sendError, setSendError ] = useState<string>('');

  async function send() {
    if (sending) return;
    setSending(true);
    setSendError('');

    const db = getFirestore();

    // 年度ごとに別フィールド(2027_sending / 2028_sending ...)へ保存する。
    // recruits 側は年度に関わらず recruits/{companyId}_2027 にまとめて持つ
    // (求人票一覧 2.jobPostings/index.tsx がここの `${year}_sending` で選択済みを判定する)。
    const sendingField = `${year}_sending`;
    const modified = companyId + '_' + "2027";

    // companies / recruits / searchIndex / progress は1つのバッチでまとめて書き込む。
    // 途中で失敗して「会社側では送付済み、学校側では未送付」のように一部だけ
    // 反映されるのを防ぐ(バッチは全部成功するか、全部失敗するかのどちらか)。
    try {
      const batch = writeBatch(db);

      batch.update(doc(db, 'companies', companyId), {
        [sendingField]: arrayUnion(...schools),
        'progress.sending': true,
      });

      batch.update(doc(db, 'recruits', modified), {
        [sendingField]: arrayUnion(...schools),
      });

      // 学校側の求人一覧(src/school/1.jobPostings)は searchIndex の sending_{year} を
      // array-contains で検索する。送付した時点で学校側に反映されるよう、ここでも追加する
      // (他の同期処理と同じく schools コレクションにある学校だけ)。searchIndex は求人票の確定時に
      // 作られるため、まだ無い場合は作らずにスキップする(後の公開・同期処理で反映される)。
      const searchIndexRef = doc(db, 'searchIndex', modified);
      const sendingWhitelist = await loadSendingWhitelist();
      const whitelisted = schools.filter((id) => sendingWhitelist.has(id));
      if (whitelisted.length > 0 && (await getDoc(searchIndexRef)).exists()) {
        batch.update(searchIndexRef, {
          [`sending_${year}`]: arrayUnion(...whitelisted),
        });
      }

      await batch.commit();
    } catch (error) {
      console.error('送付先の保存に失敗しました:', error);
      setSendError('送付先の保存に失敗しました。通信状況をご確認のうえ、もう一度お試しください。');
      setSending(false);
      return;
    }

    setCheckList([]);
    setSendModal(false);
    navigate(completePath);

    // schools/{schoolId}.2027_sending への書き込みはコメントアウト。
    // 学校側の求人一覧（src/school/1.jobPostings/index.tsx）は
    // searchIndex.sending_2027（recruits.2027_sendingから同期）を
    // array-containsで検索しており、schools側のこのフィールドはどこからも
    // 読まれていないことをコード全体を確認して検証済み。
    // また一部の学校IDが実在しない場合にupdateDoc()が
    // "No document to update"で失敗し、以降の学校への処理が止まる問題があった。
    // for (const schoolId of schools) {
    //   const schoolDocRef = doc(db, 'schools', schoolId);
    //   await updateDoc(schoolDocRef, {
    //     '2027_sending': arrayUnion(modified),
    //   });
    // }
  }

  return (
    <div className={Styles.overlay} role='dialog' aria-modal='true'>
      <div className={Styles.scrim} onClick={() => !sending && setSendModal(false)} />
      <div className={Styles.card}>
        <h3 className={Styles.title}>求人票を学校に送付する</h3>
        <p className={Styles.note}>
          一度送付した求人票は取り消すことができませんので、ご注意ください。
        </p>

        <p className={Styles.listLabel}>送付先学校一覧（{selectedNames.length}校）</p>
        <ul className={Styles.list}>
          {selectedNames.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>

        {sendError && <p className={Styles.error} role='alert'>{sendError}</p>}

        <div className={Styles.actions}>
          <button type='button' className={Styles.cancel} disabled={sending} onClick={() => setSendModal(false)}>
            戻る
          </button>
          {/* 保存が終わるまでモーダルを閉じない(失敗したらここにエラーを出す) */}
          <button type='button' className={Styles.confirm} disabled={sending} onClick={() => send()}>
            {sending ? '送付中...' : '送付する'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PopModal;
